import type { CrawlAttempt, Job } from '@zyx/shared';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ErrorState, LoadingRows } from '@/components/common/states';
import { ItemDialog, Outcome } from '@/components/indexing/item-dialog';
import { JobItems, JobRuns } from '@/components/indexing/job-items';
import { ItemLabel } from '@/components/indexing/item-label';
import { JobCard } from '@/components/indexing/job-list';
import { TimelineChart } from '@/components/indexing/timeline-chart';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useApi } from '@/hooks/use-connection';
import { formatRuntime } from '@/lib/format';
import { urls } from '@/lib/urls';

function stamp(ms: number | undefined): string {
  return ms === undefined ? '—' : new Date(ms).toLocaleString();
}

function duration(job: Job): string | undefined {
  if (job.startedAt === undefined) {
    return undefined;
  }
  return formatRuntime(Math.round(((job.finishedAt ?? Date.now()) - job.startedAt) / 1000));
}

function Tile(props: { readonly label: string; readonly value: number | string }) {
  return (
    <div className="rounded-lg border border-border px-4 py-3">
      <p className="text-xs text-muted-foreground">{props.label}</p>
      <p className="text-2xl font-semibold tabular-nums">{props.value}</p>
    </div>
  );
}

function pageOf(attempt: CrawlAttempt): string {
  try {
    const url = new URL(attempt.target);
    return `${url.pathname.replace(/^\/en\//, '')} · page ${url.searchParams.get('page') ?? '1'}`;
  } catch {
    return attempt.target;
  }
}

/** One job in full: when it ran, what its runs did over time, where discovery went, and what failed. */
export function JobPage() {
  const jobId = Number(useParams()['jobId']);
  return Number.isSafeInteger(jobId) && jobId > 0 ? <JobDetail jobId={jobId} /> : <ErrorState error={new Error('That is not a job id; job pages are /indexing/jobs/<number>.')} />;
}

function JobDetail(props: { readonly jobId: number }) {
  const { jobId } = props;
  const api = useApi();
  const [item, setItem] = useState<number | undefined>(undefined);
  const job = useQuery({ queryKey: ['jobs', 'detail', jobId], queryFn: () => api.getJob(jobId), refetchInterval: 4_000 });
  const history = useQuery({ queryKey: ['jobs', 'history', jobId], queryFn: () => api.getJobHistory(jobId), refetchInterval: 4_000 });

  if (job.isPending) {
    return <LoadingRows rows={4} />;
  }
  if (job.isError) {
    return <ErrorState error={job.error} />;
  }
  const found = job.data.job;
  const totals = history.data?.totals;

  return (
    <div className="space-y-6">
      <Link to={urls.indexing()} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="size-4" /> All jobs
      </Link>
      <JobCard job={found} />
      <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-4">
        {[
          ['Created', stamp(found.createdAt)],
          ['Started', stamp(found.startedAt)],
          ['Finished', stamp(found.finishedAt)],
          ['Ran for', duration(found) ?? '—'],
        ].map(([label, value]) => (
          <div key={label}>
            <dt className="text-xs text-muted-foreground">{label}</dt>
            <dd className="tabular-nums">{value}</dd>
          </div>
        ))}
      </dl>
      {history.isError ? <ErrorState error={history.error} /> : null}
      {totals === undefined ? null : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Tile label="Runs succeeded" value={totals.succeeded} />
          <Tile label="Failed, to retry" value={totals.failed} />
          <Tile label="Gave up" value={totals.gave_up} />
          <Tile label="Interrupted" value={totals.interrupted} />
        </div>
      )}
      {history.data === undefined ? null : (
        <>
          <section className="space-y-3">
            <h2 className="text-lg font-semibold tracking-tight">Throughput</h2>
            <TimelineChart points={history.data.timeline} stepMs={history.data.timelineStepMs} />
          </section>
          <JobItems job={found} onSelect={setItem} />
          <JobRuns jobId={jobId} onSelect={setItem} />
          {history.data.discovery.length === 0 ? null : (
            <section className="space-y-3">
              <h2 className="text-lg font-semibold tracking-tight">Discovery</h2>
              <div className="overflow-x-auto rounded-lg border border-border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>List page</TableHead>
                      <TableHead>Result</TableHead>
                      <TableHead className="text-right">New</TableHead>
                      <TableHead className="text-right">Already known</TableHead>
                      <TableHead>When</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.data.discovery.map((attempt) => (
                      <TableRow key={attempt.attemptId} className="cursor-pointer" onClick={() => setItem(attempt.itemId)}>
                        <TableCell className="font-mono text-xs">{pageOf(attempt)}</TableCell>
                        <TableCell>
                          <Outcome outcome={attempt.outcome} />
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{attempt.queuedNew}</TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground">{attempt.queuedKnown}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{new Date(attempt.finishedAt).toLocaleTimeString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <p className="text-xs text-muted-foreground">A page whose movies were all known ends discovery, unless the job walks every page.</p>
            </section>
          )}
          <section className="space-y-3">
            <h2 className="text-lg font-semibold tracking-tight">Still failing</h2>
            {history.data.failures.length === 0 ? (
              <p className="text-sm text-muted-foreground">Every URL this job ran succeeded on its latest try.</p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>URL</TableHead>
                      <TableHead>Result</TableHead>
                      <TableHead>Error</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.data.failures.map((attempt) => (
                      <TableRow key={attempt.attemptId} className="cursor-pointer" onClick={() => setItem(attempt.itemId)}>
                        <TableCell className="max-w-md text-xs">
                          <ItemLabel kind={attempt.kind} target={attempt.target} subject={attempt.subject} />
                        </TableCell>
                        <TableCell>
                          <Outcome outcome={attempt.outcome} />
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">{attempt.error ?? (attempt.httpStatus === undefined ? '—' : `HTTP ${attempt.httpStatus}`)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </section>
        </>
      )}
      <ItemDialog itemId={item} onSelect={setItem} />
    </div>
  );
}
